import React, { forwardRef, useCallback, useMemo, useState } from "react";
import { Typography, Box, IconButton, Tooltip } from "@mui/material";
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Payments as PaymentsIcon,
} from "@mui/icons-material";
import { useParams } from "react-router";
import EditableTable, {
  EditableTableColumn,
  EditableTableRef,
} from "./common/EditableTable";
import { EditableTableConfig } from "./common/types";
import { apiService } from "../../services/api";
import { formatNumber } from "../../utils/formatters";
import InfoTooltip from "../common/overlays/InfoTooltip";
import LoanAuszahlungDialog from "../pages/projectFinancing/sections/LoanAuszahlungDialog";
import {
  LoanWithPlan,
  LoanCreate,
  LoanUpdate,
  SelectOption,
  ValidationsResponse,
} from "../../types";
import { colors } from "../../styles/colors";

/** Optionen für das Abruf-Dropdown; Vorzeichenregel siehe `beginn_monate_relativ_betrieb` in types. */
const ABRUF_OPTIONS = [
  { value: 0, label: "Bei Inbetriebnahme" },
  { value: 3, label: "3 Mon. vor Betrieb" },
  { value: 6, label: "6 Mon. vor Betrieb" },
  { value: 9, label: "9 Mon. vor Betrieb" },
  { value: 12, label: "12 Mon. vor Betrieb" },
  { value: 15, label: "15 Mon. vor Betrieb" },
  { value: 18, label: "18 Mon. vor Betrieb" },
  { value: 21, label: "21 Mon. vor Betrieb" },
  { value: 24, label: "24 Mon. vor Betrieb" },
  { value: -12, label: "12 Mon. nach Betrieb" },
];

/** Tooltip-Text für das Hauptdarlehen */
const HAUPTDARLEHEN_TOOLTIP =
  "Dieses Darlehen wird automatisch angepasst, wenn sich das Fremdkapital ändert (z.B. durch Änderung der Eigenkapitalquote).";

interface LoanTableProps {
  loans: LoanWithPlan[];
  /** Optionen für den Darlehenstyp (vom Backend geladen) */
  darlehensTypOptions: SelectOption[];
  canEdit?: boolean;
  /** Projektlaufzeit in Jahren (wird an LoanAuszahlungDetail weitergereicht) */
  projektLaufzeit: number;
  onLoanDeleted?: (loanId: string) => void;
  onDataChanged?: () => void;
  /** Callback für Validierungen aus API-Responses → PageWarningsContext */
  onValidationsReceived?: (validations: ValidationsResponse) => void;
}

/**
 * Loan Table Component
 * Zeigt alle Darlehen in einer editierbaren Tabelle an
 * Verwendet EditableTable für Inline-Editing
 *
 * Features:
 * - Inline-Editing für alle Felder
 * - Stern-Icon zur Kennzeichnung des Hauptdarlehens (klickbar zum Wechseln)
 * - Tilgungsintervall ist fest auf "monatlich" gesetzt (nicht editierbar)
 * - Auszahlungsplan-Dialog über Button in eigener Spalte
 */
const LoanTable = forwardRef<EditableTableRef, LoanTableProps>(
  (
    {
      loans,
      darlehensTypOptions,
      canEdit = false,
      projektLaufzeit,
      onLoanDeleted,
      onDataChanged,
      onValidationsReceived,
    },
    ref,
  ) => {
    const { projectId } = useParams<{ projectId: string }>();
    const [isSettingHauptdarlehen, setIsSettingHauptdarlehen] = useState(false);

    const typLabels = useMemo(() => {
      const map: Record<string, string> = {};
      for (const opt of darlehensTypOptions) map[opt.value] = opt.label;
      return map;
    }, [darlehensTypOptions]);

    // State für den Auszahlungs-Dialog
    const [selectedLoanForAuszahlung, setSelectedLoanForAuszahlung] =
      useState<LoanWithPlan | null>(null);

    const handleOpenAuszahlungDialog = useCallback(
      (loan: LoanWithPlan, e: React.MouseEvent) => {
        e.stopPropagation(); // Verhindert Row-Click (Edit-Modus)
        setSelectedLoanForAuszahlung(loan);
      },
      [],
    );

    const handleCloseAuszahlungDialog = useCallback(() => {
      setSelectedLoanForAuszahlung(null);
    }, []);

    const handleAuszahlungChanged = useCallback(() => {
      onDataChanged?.();
    }, [onDataChanged]);

    /**
     * Setzt ein Darlehen als Hauptdarlehen
     * Das Backend setzt automatisch alle anderen auf is_hauptdarlehen=false
     */
    const handleSetHauptdarlehen = useCallback(
      async (loanId: string, e: React.MouseEvent) => {
        e.stopPropagation(); // Verhindert Row-Click (Edit-Modus)

        if (!projectId || isSettingHauptdarlehen) return;

        setIsSettingHauptdarlehen(true);
        try {
          // Response enthält data und validations - hier ignorieren wir die Validierungen
          await apiService.updateLoan(projectId, loanId, {
            is_hauptdarlehen: true,
          });
          onDataChanged?.();
        } catch (error) {
        } finally {
          setIsSettingHauptdarlehen(false);
        }
      },
      [projectId, isSettingHauptdarlehen, onDataChanged],
    );

    const safeLoans = Array.isArray(loans) ? loans : [];
    const hasMultipleLoans = safeLoans.length > 1;

    // Spalten-Definition mit Edit-Konfiguration (ohne Intervall)
    // Hauptdarlehen-Spalte wird nur angezeigt wenn mehr als ein Darlehen vorhanden
    const columns: EditableTableColumn[] = useMemo(() => {
      const baseColumns: EditableTableColumn[] = [
        {
          key: "typ",
          label: "Typ",
          align: "left",
          width: "18%",
          sortable: true,
          editConfig: {
            type: "dropdown",
            options: darlehensTypOptions,
            required: true,
          },
        },
        {
          key: "darlehenshoehe",
          label: "Betrag",
          align: "right",
          width: "15%",
          sortable: true,
          editConfig: {
            type: "number",
            suffix: "€",
            required: true,
            allowDecimals: false,
            min: 1,
          },
        },
        {
          key: "fremdkapitalzins",
          label: "Sollzins",
          align: "right",
          width: "12%",
          sortable: true,
          editConfig: {
            type: "number",
            suffix: "%",
            required: true,
            allowDecimals: true,
            min: 0,
          },
        },
        {
          key: "beginn_monate_relativ_betrieb",
          label: "Abruf",
          align: "right",
          width: "18%",
          sortable: true,
          editConfig: {
            type: "dropdown",
            options: ABRUF_OPTIONS,
            allowCustomValue: true,
            customValueLabel: "Individuell eingeben...",
            allowNegative: true,
            allowDecimals: false,
          },
        },
        {
          key: "laufzeit_jahre",
          label: "Laufzeit",
          align: "right",
          width: "12%",
          sortable: true,
          editConfig: {
            type: "number",
            suffix: "Jahre",
            required: true,
            allowDecimals: false,
            min: 1,
          },
        },
        {
          key: "tilgungsfreie_monate",
          label: "Tilgungsfrei",
          align: "right",
          width: "15%",
          sortable: true,
          editConfig: {
            type: "number",
            suffix: "Monate",
            allowDecimals: false,
            min: 0,
          },
        },
      ];

      // Hauptdarlehen-Spalte nur bei mehreren Darlehen anzeigen
      if (hasMultipleLoans) {
        return [
          {
            key: "is_hauptdarlehen",
            label: "", // Kein Label
            align: "center",
            width: "40px",
            sortable: false, // Kein Sortieren
          },
          ...baseColumns,
        ];
      }

      return baseColumns;
    }, [hasMultipleLoans, darlehensTypOptions]);

    // Edit-Konfiguration für die Tabelle (ohne Intervall - wird fest auf monatlich gesetzt)
    const editConfig: EditableTableConfig<LoanWithPlan> = useMemo(
      () => ({
        fields: {
          typ: {
            type: "dropdown",
            options: darlehensTypOptions,
            required: true,
          },
          darlehenshoehe: {
            type: "number",
            suffix: "€",
            required: true,
            allowDecimals: false,
            min: 1,
          },
          fremdkapitalzins: {
            type: "number",
            suffix: "%",
            required: true,
            allowDecimals: true,
            min: 0,
          },
          beginn_monate_relativ_betrieb: {
            type: "dropdown",
            options: ABRUF_OPTIONS,
            allowCustomValue: true,
            customValueLabel: "Individuell eingeben...",
            allowNegative: true,
            allowDecimals: false,
          },
          laufzeit_jahre: {
            type: "number",
            suffix: "Jahre",
            required: true,
            allowDecimals: false,
            min: 1,
          },
          tilgungsfreie_monate: {
            type: "number",
            suffix: "Monate",
            allowDecimals: false,
            min: 0,
          },
        },
        onCreate: async (data: Partial<LoanWithPlan>) => {
          if (!projectId) throw new Error("Keine Projekt-ID");

          const loanData: LoanCreate = {
            typ: data.typ || "bankdarlehen",
            darlehenshoehe: data.darlehenshoehe || 0,
            fremdkapitalzins: data.fremdkapitalzins || 0,
            // Ohne Angabe setzt der Server Bereitstellungszins und Laufzeit (Schema-Defaults).
            bereitstellungszins: data.bereitstellungszins ?? undefined,
            beginn_monate_relativ_betrieb:
              data.beginn_monate_relativ_betrieb ?? 0, // Default: bei Betriebsbeginn
            laufzeit_jahre: data.laufzeit_jahre || undefined,
            tilgungsfreie_monate: data.tilgungsfreie_monate || 0,
            tilgungsintervall: "monatlich", // Immer monatlich
          };

          const response = await apiService.createLoan(projectId, loanData);
          onValidationsReceived?.(response.validations);
          return response.data;
        },
        onUpdate: async (loanId: string, changes: Partial<LoanWithPlan>) => {
          if (!projectId) throw new Error("Keine Projekt-ID");

          // Konvertiere Partial<LoanWithPlan> zu LoanUpdate
          const updateData: LoanUpdate = {};
          if (changes.typ !== undefined) updateData.typ = changes.typ;
          if (changes.darlehenshoehe !== undefined)
            updateData.darlehenshoehe = changes.darlehenshoehe;
          if (changes.fremdkapitalzins !== undefined)
            updateData.fremdkapitalzins = changes.fremdkapitalzins;
          if (changes.bereitstellungszins !== undefined)
            updateData.bereitstellungszins = changes.bereitstellungszins;
          if (changes.beginn_monate_relativ_betrieb !== undefined)
            updateData.beginn_monate_relativ_betrieb =
              changes.beginn_monate_relativ_betrieb;
          if (changes.laufzeit_jahre !== undefined)
            updateData.laufzeit_jahre = changes.laufzeit_jahre;
          if (changes.tilgungsfreie_monate !== undefined)
            updateData.tilgungsfreie_monate = changes.tilgungsfreie_monate;
          // tilgungsintervall wird nicht mehr geändert

          const response = await apiService.updateLoan(
            projectId,
            loanId,
            updateData,
          );
          onValidationsReceived?.(response.validations);
          return response.data;
        },
        onDelete: async (loanId: string) => {
          if (!projectId) throw new Error("Keine Projekt-ID");
          const response = await apiService.deleteLoan(projectId, loanId);
          onValidationsReceived?.(response.validations);
          onLoanDeleted?.(loanId);
        },
        validateRow: (data: Partial<LoanWithPlan>) => {
          const errors: Record<string, string> = {};

          // Cross-Field-Validierung: tilgungsfreie_monate < laufzeit in Monaten
          const laufzeitMonate = (data.laufzeit_jahre ?? 0) * 12;
          const tilgungsfrei = data.tilgungsfreie_monate ?? 0;
          if (tilgungsfrei >= laufzeitMonate && laufzeitMonate > 0) {
            errors.tilgungsfreie_monate = "Muss kleiner als Laufzeit sein";
          }

          return errors;
        },
      }),
      [projectId, onLoanDeleted, darlehensTypOptions, onValidationsReceived],
    );

    // Custom Cell-Renderer für spezielle Formatierung im Anzeigemodus
    const renderCell = useCallback(
      (column: EditableTableColumn, row: LoanWithPlan, value: any) => {
        switch (column.key) {
          case "is_hauptdarlehen":
            // Stern-Icon für Hauptdarlehen in eigener Spalte
            const isHauptdarlehen = row.is_hauptdarlehen === true;

            return (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                {canEdit ? (
                  // Klickbarer Stern im Edit-Modus
                  isHauptdarlehen ? ( // Hauptdarlehen: Mit Tooltip
                    <InfoTooltip title={HAUPTDARLEHEN_TOOLTIP}>
                      <IconButton
                        size="small"
                        disabled={isSettingHauptdarlehen}
                        sx={{
                          p: 0.25,
                          color: colors.tableActions.highlight.main,
                          cursor: "inherit",
                        }}
                      >
                        <StarIcon fontSize="small" />
                      </IconButton>
                    </InfoTooltip> // Nicht-Hauptdarlehen: Ohne Tooltip, nur klickbar
                  ) : (
                    <IconButton
                      size="small"
                      onClick={(e) => handleSetHauptdarlehen(row.id, e)}
                      disabled={isSettingHauptdarlehen}
                      sx={{
                        p: 0.25,
                        color: colors.tableActions.highlight.inactive,
                        cursor: "pointer",
                        "&:hover": {
                          color: colors.tableActions.highlight.main,
                          backgroundColor: colors.tableActions.highlight.hover,
                        },
                      }}
                    >
                      <StarBorderIcon fontSize="small" />
                    </IconButton>
                  )
                ) : // Nur Anzeige im View-Modus
                isHauptdarlehen ? (
                  <InfoTooltip title={HAUPTDARLEHEN_TOOLTIP}>
                    <StarIcon
                      fontSize="small"
                      sx={{ color: colors.tableActions.highlight.main }}
                    />
                  </InfoTooltip>
                ) : (
                  <StarBorderIcon
                    fontSize="small"
                    sx={{ color: colors.tableActions.highlight.inactive }}
                  />
                )}
              </Box>
            );

          case "typ":
            return (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "medium",
                }}
              >
                {typLabels[value as string] || value}
              </Typography>
            );

          case "darlehenshoehe":
            return (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "medium",
                }}
              >
                {formatNumber(value, 0)}€
              </Typography>
            );

          case "fremdkapitalzins":
            return (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "medium",
                }}
              >
                {formatNumber(value, 2)}%
              </Typography>
            );

          case "beginn_monate_relativ_betrieb":
            // Formatierung basierend auf Vorzeichen:
            // 0 = "Bei Betrieb", positiv = "X Mon. vorher", negativ = "X Mon. nachher"
            const monate = value ?? 0;
            let abrufText: string;
            if (monate === 0) {
              abrufText = "Bei Inbetriebnahme";
            } else if (monate > 0) {
              abrufText = `${monate} Mon. vor Betrieb`;
            } else {
              abrufText = `${Math.abs(monate)} Mon. nach Betrieb`;
            }
            return (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "medium",
                }}
              >
                {abrufText}
              </Typography>
            );

          case "laufzeit_jahre":
            return (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "medium",
                }}
              >
                {value}Jahre
              </Typography>
            );

          case "tilgungsfreie_monate":
            return (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "medium",
                }}
              >
                {value}Monate
              </Typography>
            );

          default:
            return (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "medium",
                }}
              >
                {value}
              </Typography>
            );
        }
      },
      [canEdit, handleSetHauptdarlehen, isSettingHauptdarlehen],
    );

    // Custom Row Actions: Auszahlungsplan-Button in der Aktionen-Spalte
    const customRowActions = useCallback(
      (row: LoanWithPlan) => {
        // Nicht für neue Zeilen anzeigen
        if (!row.id || row.id === "__new__") {
          return null;
        }
        return (
          <Tooltip title="Auszahlungsplan bearbeiten">
            <IconButton
              size="small"
              onClick={(e) => handleOpenAuszahlungDialog(row, e)}
              sx={{
                color: colors.tableActions.payments.main,
                "&:hover": {
                  backgroundColor: colors.tableActions.payments.hover,
                  color: colors.tableActions.payments.hoverText,
                },
              }}
            >
              <PaymentsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
      [handleOpenAuszahlungDialog],
    );

    try {
      return (
        <>
          <EditableTable<LoanWithPlan>
            ref={ref}
            columns={columns}
            data={safeLoans}
            editConfig={editConfig}
            canEdit={canEdit}
            renderCell={renderCell}
            onDataChanged={onDataChanged}
            customRowActions={customRowActions}
            actionsColumnWidth="10%"
            actionsColumnAlign="right"
            variant="default"
            showBorder={false}
            minWidth={900}
            emptyMessage="Es wurden noch keine Darlehen erfasst"
            defaultSortColumn="darlehenshoehe"
            defaultSortDirection="desc"
          />

          <LoanAuszahlungDialog
            open={selectedLoanForAuszahlung !== null}
            loan={selectedLoanForAuszahlung}
            typLabels={typLabels}
            projektLaufzeit={projektLaufzeit}
            canEdit={canEdit}
            onClose={handleCloseAuszahlungDialog}
            onAuszahlungChanged={handleAuszahlungChanged}
          />
        </>
      );
    } catch (error) {
      return (
        <Box
          sx={{
            p: 2,
          }}
        >
          <Typography color="error" variant="body1">
            Fehler beim Laden der Darlehen-Tabelle:{" "}
            {error instanceof Error ? error.message : "Unbekannter Fehler"}
          </Typography>
        </Box>
      );
    }
  },
);

LoanTable.displayName = "LoanTable";

export default LoanTable;
